import { z } from 'zod';

import { RmfErrorCode } from './rmf.js';
import { CreativeErrorCode } from './creative.js';
import { IntegrationErrorCode, type ConnectorCapabilities } from './integrations.js';

/**
 * ADIM 4 — KAMPANYA OLUŞTURMA SİHİRBAZININ SÖZLEŞMESİ.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Sihirbaz YENİ bir uç açmıyor — VAR OLAN dört ucu SIRAYLA çağırıyor
 * ═══════════════════════════════════════════════════════════════════════════
 *     1. POST /rmf/campaigns        → kampanya   (PAUSED doğar)
 *     2. POST /rmf/adsets           → ad set     (PAUSED doğar)
 *     3. POST /creative/:id/publish → reklam     (kütüphanedeki kreatifle)
 *     4. POST /campaigns/status     → yayına alma (AYRI onay, para harcatır)
 *
 * Dördü de bugün canlı. Bu dosyanın işi, o dört çağrının arasındaki KARARLARI
 * tek bir yerde tutmak — hangi hedef hangi optimizasyonla gider, hangi alan
 * hangi platformda pratikte zorunludur, hata hangi ekrana dallanır.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * 🔴 REKLAM `POST /rmf/ads` İLE AÇILMAZ
 * ───────────────────────────────────────────────────────────────────────────
 * Sözleşmede reklam oluşturmanın İKİ yolu var ve ikisi de gerçek bir `Ad`
 * üretiyor:
 *
 *   (a) `POST /rmf/ads` — `creative` alanı yalnız METİN taşıyor (`assetUrl`
 *       bilerek yok, `rmfAdCreativeSchema` başlığı). Meta tarafında bu yol
 *       `image_hash`siz `link_data` gönderiyor; Meta'nın bunu kabul edip
 *       etmediği sunucudan HİÇ DOĞRULANMADI (bugüne kadar 0 kampanya açıldı).
 *   (b) `POST /creative/:id/publish` — kütüphanedeki GERÇEK görsel/videoyla,
 *       imzalama, `Ad.creativeId` bağı ve kredi muhasebesi dahil.
 *
 * Sihirbaz (b)'yi yürüyor. İkisi de çağrılırsa AYNI ad set altında İKİ reklam
 * doğar ve kullanıcı ikisini de öder. `/rmf/ads` bu akışta HİÇ çağrılmıyor;
 * onun yeri, platformda ZATEN duran bir kreatifi bağlamak
 * (`existingCreativeRef`) — sihirbazın sormadığı bir soru.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * 🔴 KATALOGLAR ELLE YAZILDI — TÜRETİLMEDİ
 * ───────────────────────────────────────────────────────────────────────────
 * `createCampaignSchema.objective` serbest bir dize (1..64) ve normalize
 * EDİLMEMESİ bilinçli bir karar. Yani seçilebilir hedeflerin listesi
 * sözleşmede YOK, hiçbir uçta yayımlı DEĞİL ve `packages/shared`ta bir
 * karşılığı yoktu. Sihirbaz kullanıcıya serbest metin kutusu sunamaz —
 * `OUTCOME_TRAFFIC` yazmasını beklemek ürün değil.
 *
 * Bu yüzden aşağıdaki üç katalog (hedef, optimizasyon hedefi, faturalama
 * olayı) BU DOSYADA ELLE YAZILDI. Bunlar bir sözleşmenin kopyası değil,
 * BAĞIMSIZ bir bilgi kaynağı ve bu ayrımın yazılı olması şart: sözleşmeden
 * türetilebilecek bir liste elle yazılırsa bir gün sessizce ayrışır.
 * Doğrulayan tek merci PLATFORMUN KENDİSİ — yanlış bir üçlü gönderilirse
 * adapter değil Meta reddediyor ve gerekçe `providerMessage` ile geliyor
 * (`meta.connector.ts` `createAdSet` başlığı: "optimization_goal,
 * billing_event ve promoted_object ÜÇÜ BİRDEN tutarlı olmak zorunda").
 *
 * Katalog bu yüzden DAR tutuldu: her satırı doğrulanabilir, yaygın ve
 * tehlikesiz olanlar. Eksik bir hedefe ihtiyaç duyulursa buraya EKLENİR;
 * kullanıcıya serbest metin AÇILMAZ.
 *
 * ⚠️ Normalize etme İDDİASI YOK. Değerler platformun KENDİ sözcükleri
 * (`OUTCOME_TRAFFIC`, `SEARCH`) ve platformlar arasında hiçbir eşleme
 * kurulmuyor — Meta'nın `OUTCOME_TRAFFIC`i ile Google'ın `SEARCH`i aynı
 * satırda değil, aynı listede bile değil.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Yetenek kapısı — sihirbaz hangi hesapta AÇILIR
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `ConnectorCapabilities.canCreateEntities` ile `available` AYRI iki soru ve
 * arayüz onlara AYRI cevap veriyor (`RmfErrorCode.PLATFORM_CANNOT_CREATE`
 * yorumu):
 *
 *   · `available: false`          → kart "yakında" gösterilir.
 *   · `canCreateEntities: false`  → "Kampanya oluştur" düğmesi HİÇ GÖSTERİLMEZ.
 *
 * 22 Ağu 2026 değerleri: META `true`, GOOGLE `true`, LINKEDIN `true`,
 * TIKTOK `false`, TWITTER `false`. Yani bugün sihirbaz yalnız Meta hesabında
 * gerçekten çalışabiliyor (Google'ın adapter'ı var ama bağlı hesap yok).
 * 29 Eyl 2026 (Faz 2): TWITTER `true`, TIKTOK `true` — güvenli doğum protokolleri
 * (`twitter.hierarchy.ts`, `tiktok.birth.ts`).
 * 29 Eyl 2026 (Faz 5): TikTok ve X hedefleri aşağıdaki katalogda VE brief'in
 * `GOAL_OBJECTIVE`inde — sayfa akışı beş platformu da açıyor.
 *
 * 🔴 BU BAYRAK KODA GÖMÜLMEZ. Kaynak `GET /integrations/platforms` yanıtındaki
 * `PlatformCard.capabilities`. Elle bir platform listesi yazmak, adapter
 * yazıldığı gün ekranın yalan söylemesi demek.
 */
export function canPlatformCreate(capabilities: ConnectorCapabilities | undefined): boolean {
  return Boolean(capabilities?.available && capabilities.canCreateEntities);
}

// ═══════════════════════════════════════════════════════════════════════════
// Adımlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sihirbazın adımları — akışın TANIMI burada, bileşende değil.
 *
 * ⚠️ Adım sırası dolu daire içinde bir RAKAM ROZETİ olarak basılmaz; sessiz
 * bir üst etiket olarak yazılır ("ADIM 2/5"). Bu bir tasarım kararı ve
 * projede daha önce verildi.
 *
 * `launch` neden ayrı bir adım: oluşturulan her şey `PAUSED` doğuyor
 * (`rmf.ts` başlığı) ve yayına almak AYRI bir uç, AYRI bir `Idempotency-Key`,
 * AYRI bir denetim satırı. Onu son adımın içine gizlemek, "kaydet"e basan
 * kullanıcının farkında olmadan para harcamaya başlaması demekti.
 */
export const WIZARD_STEPS = [
  /// Hesap seçimi. `canCreateEntities: false` olan platformların hesapları
  /// listede GÖRÜNÜR ama seçilemez ve sebebi yanında yazar — hiç göstermemek
  /// "hesabım nerede" sorusunu doğurur.
  'account',
  /// Kampanya: ad, hedef, bütçe, özel reklam kategorisi.
  'campaign',
  /// Ad set: ad, bütçe, optimizasyon, hedefleme, tarih aralığı.
  'adset',
  /// Kreatif seçimi + reklam metni + hedef adres + marka kimliği.
  'creative',
  /// Özet ve yayına alma onayı. Burada "atla" da meşru bir seçim:
  /// duraklatılmış kampanya panelde durur ve sonra yayına alınabilir.
  'launch',
] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

/**
 * Bir turda yayınlanabilecek en fazla kreatif.
 *
 * Tavan var çünkü her seçim GERÇEK bir reklam açıyor ve kredi harcıyor:
 * ızgarada yanlışlıkla "tümünü seç"e benzeyen bir hareket, kullanıcıya
 * ödemediği bir faturayı ödetirdi. On, bir ad set altında elle yönetilebilir
 * kreatif sayısının üst sınırı — üstü zaten A/B testi işi, sihirbazın değil.
 */
export const WIZARD_MAX_CREATIVES = 10;

/**
 * `?adIds=a,b,c` — adres çubuğundaki çoklu reklam kimliği.
 *
 * Ham `z.array(z.uuid())` KULLANILAMAZ: adresten gelen şey her zaman tek bir
 * DİZE ve dizi şeması onu reddederdi (`uuidListParam`in başındaki tuzağın
 * aynısı, farklı ayraçla). Bölme burada yapılıyor ki adresi okuyan tek yer
 * bu şema olsun.
 */
const wizardAdIdsParam = z
  .string()
  .transform((value) =>
    value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0),
  )
  .pipe(z.array(z.uuid()).min(1).max(WIZARD_MAX_CREATIVES));

/**
 * Sihirbazın ADRESTE tutulan durumu.
 *
 * 🔴 BU ALANLAR OLMADAN SİHİRBAZ KURTARILAMAZ. Oluşturulan kampanya ve ad set
 * GERÇEK kayıtlar (platformda gerçek nesneler) ama "hangi adımdayım" bilgisi
 * hiçbir yerde saklanmıyordu. Kullanıcı sekmeyi kapatıp dönerse ya baştan
 * başlar — ki bu İKİNCİ BİR KAMPANYA açar — ya da elle bulmak zorunda kalır.
 *
 * `localStorage` yerine adres seçildi: paylaşılabilir, geri tuşuyla çalışır,
 * cihaz başına değil oturum başına doğru, ve şema değişikliği istemiyor.
 * İkinci adım patlarsa (yetim, duraklatılmış kampanya) kullanıcı aynı
 * adresle DEVAM EDER, baştan başlamaz.
 */
export const wizardStateSchema = z.object({
  step: z.enum(WIZARD_STEPS).default('account'),
  /// BİZİM `AdAccount.id`imiz.
  adAccountId: z.uuid().optional(),
  /// Oluşturulmuş kampanyanın BİZİM kimliğimiz. Dolu ise 1. çağrı BİTTİ.
  campaignId: z.uuid().optional(),
  /// Oluşturulmuş ad set. Dolu ise 2. çağrı BİTTİ.
  adSetId: z.uuid().optional(),
  /**
   * Yayınlanmış reklamlar. Dolu ise 3. çağrı BİTTİ, geriye yalnız yayına alma
   * kaldı.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * 🔴 ÇOĞUL — tekil `adId` sessizce reklam DÜŞÜRÜYORDU
   * ═══════════════════════════════════════════════════════════════════════
   * Kreatif adımı artık kütüphaneden birden fazla seçime izin veriyor ve her
   * seçim AYNI ad set altında ayrı bir reklam açıyor. Bu uydurulmuş bir
   * yetenek değil, yayınlama sözleşmesinin açıkça tarif ettiği durum
   * (`publishSchema.identityRef`: "mevcut bir ad set'e ikinci kreatif
   * eklerken doğru davranış budur").
   *
   * Alan tekil kalsaydı yayına alma adımı yalnız İLK reklamı `ACTIVE`
   * yapardı; kalanlar `PAUSED` durur, hiçbir ekran bunu söylemez ve kullanıcı
   * kredisini ödediği kreatiflerin neden yayında olmadığını anlayamazdı.
   *
   * Adreste virgülle ayrılmış duruyor (`?adIds=a,b,c`). Tekrar eden anahtar
   * (`?adIds=a&adIds=b`) DEĞİL: `buildQuery` (apps/web) tek değer yazıyor ve
   * sıralı/kararlı bir adres üretmesi bilinçli.
   */
  adIds: wizardAdIdsParam.optional(),
});
export type WizardState = z.infer<typeof wizardStateSchema>;

/**
 * Adres durumundan gerçek adımı türet.
 *
 * `step` parametresine körü körüne güvenilmez: kullanıcı `?step=creative`
 * yazıp kampanyasız gelebilir. Tamamlanmış çağrılar tek gerçek — onlardan
 * geri kalan ilk adım seçilir.
 */
export function resolveWizardStep(state: WizardState): WizardStep {
  if (!state.adAccountId) return 'account';
  if (!state.campaignId) return 'campaign';
  if (!state.adSetId) return 'adset';
  if (!state.adIds || state.adIds.length === 0) return 'creative';
  return 'launch';
}

// ═══════════════════════════════════════════════════════════════════════════
// Hedef kataloğu — ELLE YAZILDI (bkz. dosya başlığı)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bir hedefin sunduğu optimizasyon/faturalama çifti.
 *
 * `optimizationGoal` ve `billingEvent` `createAdSetSchema`da isteğe bağlı ama
 * META'DA ZORUNLU (servis platforma göre karar veriyor) ve GOOGLE'da
 * gönderilirse `FIELD_NOT_SUPPORTED_ON_PLATFORM` ile REDDEDİLİYOR. Yani aynı
 * iki alan bir platformda zorunlu, diğerinde yasak — bu yüzden katalog
 * platform kırılımlı.
 */
export interface OptimizationOption {
  /// Platformun kendi sözcüğü. `createAdSetSchema.optimizationGoal`a bu gider.
  optimizationGoal: string;
  /// Platformun kendi sözcüğü. `createAdSetSchema.billingEvent`e bu gider.
  billingEvent: string;
  /// i18n anahtarı — düz metin DEĞİL. Ekran çevirir, sunucu değil.
  labelKey: string;
  /**
   * Bu seçim `promotedObject` (piksel + olay) İSTİYOR mu.
   *
   * `OFFSITE_CONVERSIONS` piksel olmadan Meta tarafından reddediliyor.
   * Adapter bunu doğrulamıyor — reddi platform veriyor. Arayüz alanı zorunlu
   * kılarak kullanıcıyı o reddin önünde durduruyor.
   */
  requiresPromotedObject: boolean;
  /**
   * Piksel optimizasyonunda optimize edilecek OLAY (`promoted_object.
   * custom_event_type`) — yalnız `requiresPromotedObject: true` seçeneklerde.
   *
   * 🔴 29 Eyl 2026'ya kadar YOKTU: brief pikseli olaysız gönderiyordu
   * (`promotedObject: { pixelId }`). Meta `OFFSITE_CONVERSIONS` için
   * `pixel_id` + `custom_event_type` İKİSİNİ BİRDEN istiyor (ODAX tablosu);
   * olaysız ad set reddediliyor ve zincir boş kampanya bırakıyordu.
   */
  customEventType?: string;
  /**
   * Meta `destination_type` — ODAX tablosu bazı optimizasyonları bir hedef
   * yerine bağlıyor (gönderi etkileşimi → `ON_POST`). Boş = web sitesi.
   * RMF bağlayıcısı sihirbaz bu alanı taşımasa da aynı kuralı uyguluyor
   * (`meta.connector.ts` → `createAdSet`).
   */
  destinationType?: string;
  /**
   * TikTok `promotion_type` — reklam setinde ZORUNLU (REACH/VIDEO_VIEWS/
   * ENGAGEMENT hariç) ve sözleşmede tipli alanı yok: brief onu
   * `platformOptions.promotionType` olarak taşıyor (`tiktok.rmf.ts` izin
   * listesi). Boş = TikTok varsayılanı (tanıtım türü istenmeyen hedefler).
   */
  promotionType?: string;
  /**
   * TikTok `promotion_target_type` — yalnız LEAD_GENERATION hedefinde:
   * `INSTANT_PAGE` (TikTok Instant Form) ya da `EXTERNAL_WEBSITE` (web formu).
   * Brief onu `platformOptions.promotionTargetType` olarak taşıyor
   * (`tiktok.rmf.ts` izin listesi; bağlayıcı Instant Form'lu reklamı yalnız
   * `INSTANT_PAGE` setinde kabul ediyor).
   */
  promotionTargetType?: string;
}

export interface CampaignObjectiveOption {
  /// Platformun KENDİ hedef sözcüğü. `createCampaignSchema.objective`e AYNEN
  /// gider; normalize edilmez, çevrilmez.
  value: string;
  /// i18n anahtarı (`campaignWizard.objective.<value>`).
  labelKey: string;
  /// i18n anahtarı — bir cümlelik açıklama.
  descriptionKey: string;
  /// Bu hedefte sunulacak optimizasyon/faturalama çiftleri. Google'da BOŞ:
  /// o platformda bu iki alan gönderilemez.
  optimizations: readonly OptimizationOption[];
}

/**
 * META hedefleri (ODAX sözcükleri).
 *
 * Beş hedef seçildi; Meta'nın tamamı değil. `OUTCOME_APP_PROMOTION` DIŞARIDA
 * çünkü uygulama tanıtımı `promoted_object.application_id` istiyor ve
 * `rmfTargetingSchema`nın ne o alanı ne de mağaza seçimini karşılığı var —
 * listeye koymak, son adımda platformun reddedeceği bir yol açmak olurdu.
 */
const META_OBJECTIVES: readonly CampaignObjectiveOption[] = [
  {
    value: 'OUTCOME_TRAFFIC',
    labelKey: 'campaignWizard.objective.OUTCOME_TRAFFIC',
    descriptionKey: 'campaignWizard.objectiveHint.OUTCOME_TRAFFIC',
    optimizations: [
      {
        optimizationGoal: 'LINK_CLICKS',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.LINK_CLICKS',
        requiresPromotedObject: false,
      },
      {
        optimizationGoal: 'LANDING_PAGE_VIEWS',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.LANDING_PAGE_VIEWS',
        requiresPromotedObject: false,
      },
    ],
  },
  {
    value: 'OUTCOME_AWARENESS',
    labelKey: 'campaignWizard.objective.OUTCOME_AWARENESS',
    descriptionKey: 'campaignWizard.objectiveHint.OUTCOME_AWARENESS',
    optimizations: [
      {
        optimizationGoal: 'REACH',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.REACH',
        requiresPromotedObject: false,
      },
      {
        optimizationGoal: 'IMPRESSIONS',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.IMPRESSIONS',
        requiresPromotedObject: false,
      },
    ],
  },
  {
    value: 'OUTCOME_ENGAGEMENT',
    labelKey: 'campaignWizard.objective.OUTCOME_ENGAGEMENT',
    descriptionKey: 'campaignWizard.objectiveHint.OUTCOME_ENGAGEMENT',
    optimizations: [
      {
        optimizationGoal: 'POST_ENGAGEMENT',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.POST_ENGAGEMENT',
        requiresPromotedObject: false,
        destinationType: 'ON_POST',
      },
    ],
  },
  {
    value: 'OUTCOME_LEADS',
    labelKey: 'campaignWizard.objective.OUTCOME_LEADS',
    descriptionKey: 'campaignWizard.objectiveHint.OUTCOME_LEADS',
    optimizations: [
      {
        optimizationGoal: 'OFFSITE_CONVERSIONS',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.OFFSITE_CONVERSIONS',
        requiresPromotedObject: true,
        customEventType: 'LEAD',
      },
      /* 29 Eyl 2026 (Faz 5) — ANINDA FORM (instant form). ODAX: `ON_AD` +
         `LEAD_GENERATION` + `promoted_object.page_id` (piksel YOK); form
         kimliği kreatifin CTA'sında (`lead_gen_form_id`). Brief'in "potansiyel
         müşteri" amacı bu seçeneği kullanıyor (`GOAL_OPTIMIZATION`); piksel
         yolu (yukarıdaki) web sitesi formu isteyenler için duruyor. */
      {
        optimizationGoal: 'LEAD_GENERATION',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.LEAD_GENERATION',
        requiresPromotedObject: false,
        destinationType: 'ON_AD',
      },
    ],
  },
  {
    value: 'OUTCOME_SALES',
    labelKey: 'campaignWizard.objective.OUTCOME_SALES',
    descriptionKey: 'campaignWizard.objectiveHint.OUTCOME_SALES',
    optimizations: [
      {
        optimizationGoal: 'OFFSITE_CONVERSIONS',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.OFFSITE_CONVERSIONS',
        requiresPromotedObject: true,
        customEventType: 'PURCHASE',
      },
    ],
  },
];

/**
 * GOOGLE — TEK seçenek ve bu bir eksiklik değil, ADAPTER'IN SÖZLEŞMESİ.
 *
 * `google.create.ts` içindeki `CREATABLE_CHANNEL_TYPES` tablosunda tek bir
 * satır var: `SEARCH`. Başka bir değer gönderilirse adapter, platforma hiç
 * gitmeden "Google'da bu uçtan yalnız arama (SEARCH) kampanyası
 * oluşturulabiliyor" diye reddediyor. Yani liste burada UYDURULMADI, adapter
 * tablosundan OKUNDU ve tek satır olması onun tek satır olmasından.
 *
 * Optimizasyon listesi BOŞ ve bu da bilinçli: Google'da bu iki alan
 * gönderilemez (`FIELD_NOT_SUPPORTED_ON_PLATFORM`).
 */
const GOOGLE_OBJECTIVES: readonly CampaignObjectiveOption[] = [
  {
    value: 'SEARCH',
    labelKey: 'campaignWizard.objective.SEARCH',
    descriptionKey: 'campaignWizard.objectiveHint.SEARCH',
    optimizations: [],
  },
];

/**
 * LINKEDIN hedefleri — TEKİL sözcükler (29 Eyl 2026, Faz 2).
 *
 * 🔴 Brief akışı bu katalog YOKKEN kırıktı: `compileBrief` optimizasyonu
 * `wizardOptimizations()[0]`dan okuyor, LinkedIn satırı boş olduğu için
 * `optimizationGoal`/`billingEvent` boş gidiyor ve adapter "faturalama olayı
 * tanınmadı" ile her reklam setini düşürüyordu.
 *
 * Değerler UYDURULMADI: hedef listesi ve ilk (varsayılan) optimizasyon
 * `@mentorivo/shared/platform-ops` → `LINKEDIN_OBJECTIVE_MATRIX.briefDefault`
 * (şartname §2.4) ile AYNI; `linkedin.ops.spec.ts` ikisini karşılaştırıyor.
 * `billingEvent` LinkedIn'in kendi `costType` sözcüğü (adapter onu olduğu gibi
 * tanıyor). Otomatik teklifte CPM/CPV dokümanda tablolanmamış (MEASURE) —
 * LinkedIn reddederse ret görünür olur.
 */
const LINKEDIN_OBJECTIVES: readonly CampaignObjectiveOption[] = (
  [
    ['BRAND_AWARENESS', 'MAX_IMPRESSION', 'CPM'],
    ['WEBSITE_VISIT', 'MAX_CLICK', 'CPM'],
    ['ENGAGEMENT', 'MAX_CLICK', 'CPM'],
    ['VIDEO_VIEW', 'MAX_VIDEO_VIEW', 'CPV'],
    ['LEAD_GENERATION', 'MAX_LEAD', 'CPM'],
    ['WEBSITE_CONVERSION', 'MAX_CONVERSION', 'CPM'],
  ] as const
).map(([value, optimizationGoal, billingEvent]) => ({
  value,
  labelKey: `campaignWizard.objective.${value}`,
  descriptionKey: `campaignWizard.objectiveHint.${value}`,
  optimizations: [
    {
      optimizationGoal,
      billingEvent,
      labelKey: `campaignWizard.optimization.${optimizationGoal}`,
      /* LinkedIn'de dönüşüm/lead reklam SETİNDE piksel istemiyor: dönüşüm
         kuralı hesapta tanımlanıp bağlanıyor, lead formu reklama ekleniyor —
         ikisini de aktifleştirme ön kontrolü zorunlu tutuyor. */
      requiresPromotedObject: false,
    },
  ],
}));

/**
 * TIKTOK hedefleri (29 Eyl 2026, Faz 5) — RMF yolunun AÇABİLDİĞİ kadarı.
 *
 * Her satır `tiktok.build.ts` → `adGroupBody`nin kabul ettiği üçlü:
 * hedef × optimizasyon (`GOALS_BY_OBJECTIVE`) × faturalama (`billingForGoal`,
 * TikTok faturalamayı optimizasyondan TÜRETİYOR ve uyuşmayanı reddediyor) ×
 * tanıtım türü. Kilit: `brief-tiktok-chain.spec.ts` her seçeneği adapter'ın
 * kendi gövde kurucusundan geçiriyor — tablo elle yazıldı, doğrulaması değil.
 *
 * LEAD_GENERATION (29 Eyl 2026, Faz 5 son tur) — YERLİ Instant Form:
 * reklam seti `promotion_type: LEAD_GENERATION` + `promotion_target_type:
 * INSTANT_PAGE`, reklamın `page_id`si formun kimliği (`leadFormRef`). 🔴 TikTok
 * API'si formu OLUŞTURMUYOR (`tiktok.leadform.create` dürüst ret döndürüyor):
 * kullanıcı mevcut formu seçer (`tiktok.leadform.list`), yeni form ve LeadAds
 * şartı TikTok Ads Manager'da. Web sitesi formu (`EXTERNAL_WEBSITE`) brief'te
 * YOK — piksel/olay kuralı bu hedefte doğrulanmadı; web formu isteyen
 * WEB_CONVERSIONS + `FORM` olayını seçer (gelişmiş akış / ajan).
 *
 * DIŞARIDA bırakılanlar ve sebebi (katalog DAR — `CAMPAIGN_OBJECTIVES` başlığı):
 *   · APP_PROMOTION — `app_id` + `app_promotion_type` brief'te yok.
 *   · PRODUCT_SALES — katalog/TikTok Shop allowlist'i.
 *   · ENGAGEMENT — takipçi/profil ziyareti; brief'in dört amacından hiçbiri değil.
 *   · VALUE / AUTOMATIC_VALUE_OPTIMIZATION — allowlist (değer optimizasyonu).
 */
const TIKTOK_OBJECTIVES: readonly CampaignObjectiveOption[] = [
  {
    value: 'REACH',
    labelKey: 'campaignWizard.objective.REACH',
    descriptionKey: 'campaignWizard.objectiveHint.REACH',
    optimizations: [
      {
        optimizationGoal: 'REACH',
        billingEvent: 'CPM',
        labelKey: 'campaignWizard.optimization.REACH',
        requiresPromotedObject: false,
      },
    ],
  },
  {
    value: 'TRAFFIC',
    labelKey: 'campaignWizard.objective.TRAFFIC',
    descriptionKey: 'campaignWizard.objectiveHint.TRAFFIC',
    optimizations: [
      {
        optimizationGoal: 'CLICK',
        billingEvent: 'CPC',
        labelKey: 'campaignWizard.optimization.CLICK',
        requiresPromotedObject: false,
        promotionType: 'WEBSITE',
      },
      {
        optimizationGoal: 'TRAFFIC_LANDING_PAGE_VIEW',
        billingEvent: 'OCPM',
        labelKey: 'campaignWizard.optimization.TRAFFIC_LANDING_PAGE_VIEW',
        requiresPromotedObject: false,
        promotionType: 'WEBSITE',
      },
    ],
  },
  {
    value: 'VIDEO_VIEWS',
    labelKey: 'campaignWizard.objective.VIDEO_VIEWS',
    descriptionKey: 'campaignWizard.objectiveHint.VIDEO_VIEWS',
    optimizations: [
      {
        optimizationGoal: 'ENGAGED_VIEW',
        billingEvent: 'CPV',
        labelKey: 'campaignWizard.optimization.ENGAGED_VIEW',
        requiresPromotedObject: false,
      },
    ],
  },
  {
    value: 'WEB_CONVERSIONS',
    labelKey: 'campaignWizard.objective.WEB_CONVERSIONS',
    descriptionKey: 'campaignWizard.objectiveHint.WEB_CONVERSIONS',
    optimizations: [
      {
        /* 🔴 `CONVERT` piksel + olay İSTİYOR (`adGroupBody` → PIXEL_REQUIRED).
           Olay burada VARSAYILAN (satın alma); brief amaca göre değiştiriyor
           (`GOAL_OPTIMIZATION`: potansiyel müşteri → `FORM`) ve kullanıcı
           seçebiliyor (`BRIEF_GAP.TIKTOK_OPTIMIZATION_EVENT`). */
        optimizationGoal: 'CONVERT',
        billingEvent: 'OCPM',
        labelKey: 'campaignWizard.optimization.CONVERT',
        requiresPromotedObject: true,
        customEventType: 'SHOPPING',
        promotionType: 'WEBSITE',
      },
      {
        optimizationGoal: 'CLICK',
        billingEvent: 'CPC',
        labelKey: 'campaignWizard.optimization.CLICK',
        requiresPromotedObject: false,
        promotionType: 'WEBSITE',
      },
    ],
  },
  {
    value: 'LEAD_GENERATION',
    labelKey: 'campaignWizard.objective.LEAD_GENERATION',
    descriptionKey: 'campaignWizard.objectiveHint.LEAD_GENERATION',
    optimizations: [
      {
        /* 🔴 Piksel YOK: Instant Form TikTok içinde doluyor, dönüşüm formun
           gönderimi. `billingForGoal('LEAD_GENERATION')` = OCPM. Form kimliği
           reklam adımında (`leadFormRef` → `page_id`). */
        optimizationGoal: 'LEAD_GENERATION',
        billingEvent: 'OCPM',
        labelKey: 'campaignWizard.optimization.LEAD_GENERATION',
        requiresPromotedObject: false,
        promotionType: 'LEAD_GENERATION',
        promotionTargetType: 'INSTANT_PAGE',
      },
    ],
  },
];

/**
 * X (TWITTER) hedefleri (29 Eyl 2026, Faz 5).
 *
 * Optimizasyonlar X ajanının matrisinin (`X_GOALS_BY_OBJECTIVE`,
 * `platform-ops/x.ops.ts`) brief'e giren satırları. 🔴 İçe AKTARILMIYOR,
 * yazılıyor: `platform-ops` kök pakete bilerek girmiyor (yüzlerce Zod şeması,
 * panelin her sayfası kök paketi yüklüyor — `platform-ops/index.ts` başlığı).
 * Kopya `brief-x-chain.spec.ts`te matrisle ve adapter'ın `goal` → hedef
 * tablosuyla (`twitter.create.ts` → `GOAL_OBJECTIVE`) KİLİTLİ: X'te hedef
 * reklam SETİNİN alanı ve adapter onu `goal`dan geri türetiyor.
 *
 * Faturalama `AUTO`: X `pay_by`ı hedefe göre kendisi seçiyor; adapter `AUTO`yu
 * "gönderme" diye okuyor. Dönüşüm (`WEBSITE_CONVERSIONS`) web olay etiketi
 * istiyor → `requiresPromotedObject` (adapter `promotedObject.pixelId`yi
 * `primary_web_event_tag`e yazıyor).
 *
 * DIŞARIDA: APP_INSTALLS/APP_ENGAGEMENTS (mağaza kimlikleri), PREROLL_VIEWS
 * (içerik kategorisi/yayıncı seçimi), FOLLOWERS (tanıtılan HESAP ürünü —
 * brief gönderi tanıtıyor).
 */
export const TWITTER_BRIEF_GOALS_BY_OBJECTIVE: Readonly<Record<string, readonly string[]>> = {
  WEBSITE_CLICKS: ['LINK_CLICKS', 'SITE_VISITS', 'WEBSITE_CONVERSIONS'],
  REACH: ['MAX_REACH', 'REACH_WITH_ENGAGEMENT'],
  ENGAGEMENTS: ['ENGAGEMENT'],
  VIDEO_VIEWS: ['VIDEO_VIEW', 'VIEW_3S_100PCT', 'VIEW_6S', 'VIEW_15S'],
};

const TWITTER_OBJECTIVES: readonly CampaignObjectiveOption[] = Object.entries(
  TWITTER_BRIEF_GOALS_BY_OBJECTIVE,
).map(
  ([value, goals]): CampaignObjectiveOption => ({
    value,
    labelKey: `campaignWizard.objective.${value}`,
    descriptionKey: `campaignWizard.objectiveHint.${value}`,
    optimizations: goals.map((goal) => ({
      optimizationGoal: goal,
      billingEvent: 'AUTO',
      labelKey: `campaignWizard.optimization.${goal}`,
      requiresPromotedObject: goal === 'WEBSITE_CONVERSIONS',
    })),
  }),
);

/**
 * Platform → hedef kataloğu.
 *
 * ⚠️ `AdPlatform`ın TAMAMI DEĞİL: OPENAI yok (adapter yok). Katalogda olmayan
 * platformda `wizardObjectives` boş dizi döndürür ve arayüz "hedef listesi
 * tanımlı değil" der — SESSİZCE boş bir açılır kutu göstermez.
 */
export const CAMPAIGN_OBJECTIVES: Readonly<Record<string, readonly CampaignObjectiveOption[]>> = {
  META: META_OBJECTIVES,
  GOOGLE: GOOGLE_OBJECTIVES,
  LINKEDIN: LINKEDIN_OBJECTIVES,
  TIKTOK: TIKTOK_OBJECTIVES,
  TWITTER: TWITTER_OBJECTIVES,
};

export function wizardObjectives(platform: string): readonly CampaignObjectiveOption[] {
  return CAMPAIGN_OBJECTIVES[platform] ?? [];
}

export function wizardOptimizations(
  platform: string,
  objective: string,
): readonly OptimizationOption[] {
  return wizardObjectives(platform).find((o) => o.value === objective)?.optimizations ?? [];
}

// ═══════════════════════════════════════════════════════════════════════════
// Özel reklam kategorileri (Meta)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `createCampaignSchema.specialAdCategories` seçenekleri.
 *
 * 🔴 YANLIŞ BİLDİRMEK HESABIN KAPANMASINA YOL AÇABİLİYOR. Bu yüzden alan
 * "isteğe bağlı bir etiket" gibi sunulmaz: ekranda ne olduğunu açıklayan bir
 * not ile birlikte ve hiçbiri seçilmediğinde AÇIKÇA "hiçbiri" seçili görünür.
 * Boş dizi = hiçbiri (`meta.connector.ts`: boş dizi olsa bile gönderilmek
 * zorunda). Google'da karşılığı yok ve adapter alanı kullanmıyor.
 *
 * Değerler Meta'nın kendi sözcükleri; ELLE yazıldı (bkz. dosya başlığı).
 *
 * 🔴 29 Eyl 2026: `CREDIT` → `FINANCIAL_PRODUCTS_SERVICES`. Meta `CREDIT`i
 * 14 Oca 2025'te yeni kategoriyle değiştirdi (spec §0.1 #3); eski değer
 * yalnız geriye dönük okunuyor. Bağlayıcı eski kaydı gönderirken
 * `LEGACY_SPECIAL_AD_CATEGORY` ile çeviriyor — kayıtlı bir `CREDIT` beyanı
 * sessizce DÜŞMÜYOR, yeni adıyla gidiyor.
 */
export const SPECIAL_AD_CATEGORIES = [
  'FINANCIAL_PRODUCTS_SERVICES',
  'EMPLOYMENT',
  'HOUSING',
  'ISSUES_ELECTIONS_POLITICS',
] as const;
export type SpecialAdCategory = (typeof SPECIAL_AD_CATEGORIES)[number];

/// Eski kategori adı → Meta'nın bugünkü adı.
export const LEGACY_SPECIAL_AD_CATEGORY: Readonly<Record<string, SpecialAdCategory>> = {
  CREDIT: 'FINANCIAL_PRODUCTS_SERVICES',
};

/**
 * Konut / istihdam / finans (HEC-F) — hedefleme kısıtlı kategoriler: cinsiyet
 * yok, yaş 18–65 sabit, posta kodu yok, 15 milden dar yarıçap yok, benzer
 * kitle yok (Meta "special ad category" rehberi).
 */
export const META_HECF_CATEGORIES: readonly string[] = [
  'HOUSING',
  'EMPLOYMENT',
  'FINANCIAL_PRODUCTS_SERVICES',
  'CREDIT',
];

/**
 * AB ülkeleri — AB Dijital Hizmetler Yasası (DSA): AB'yi kapsayan ad set'te
 * reklamın yararlanıcısı (`dsa_beneficiary`) ve ödeyeni (`dsa_payor`)
 * beyan edilmeli, yoksa reklam yayınlanmıyor (spec §2.2.4). TR'den AB'ye
 * reklam veren her kullanıcıyı etkiliyor.
 */
export const META_EU_COUNTRIES: readonly string[] = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
];

// ═══════════════════════════════════════════════════════════════════════════
// META — ODAX hedef × hedef yeri × optimizasyon × faturalama matrisi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 TEK KAYNAK — hem platform operasyon kataloğu (`platform-ops/meta.ops.ts`,
 * Zod enum'ları) hem Meta bağlayıcısının ops işleyicileri (kombinasyon
 * doğrulaması) BURADAN okuyor. Sihirbazın `META_OBJECTIVES` listesi bunun
 * DAR bir alt kümesi (arayüz beş hedef sunuyor); tam katalog burada.
 *
 * Neden ağa çıkmadan doğruluyoruz: Meta eksik/yanlış alanı TEK TEK söylüyor
 * ve her ret yarım bir zincir (içi boş kampanya) bırakıyordu (hafıza:
 * meta-reklam-yayin-zinciri). Matris ihlali Meta'ya hiç gitmemeli.
 *
 * Kaynak: Marketing API "ad-campaign-group" ODAX tablosu + "billing events"
 * tablosu (platform-specs/meta.md §2.2.1–2.2.3, 29 Eyl 2026).
 */
export const META_ODAX_OBJECTIVES = [
  'OUTCOME_AWARENESS',
  'OUTCOME_TRAFFIC',
  'OUTCOME_ENGAGEMENT',
  'OUTCOME_LEADS',
  'OUTCOME_APP_PROMOTION',
  'OUTCOME_SALES',
] as const;
export type MetaOdaxObjective = (typeof META_ODAX_OBJECTIVES)[number];

export const META_OPTIMIZATION_GOALS = [
  'REACH',
  'IMPRESSIONS',
  'AD_RECALL_LIFT',
  'THRUPLAY',
  'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS',
  'LINK_CLICKS',
  'LANDING_PAGE_VIEWS',
  'QUALITY_CALL',
  'POST_ENGAGEMENT',
  'PAGE_LIKES',
  'EVENT_RESPONSES',
  'CONVERSATIONS',
  'LEAD_GENERATION',
  'QUALITY_LEAD',
  'OFFSITE_CONVERSIONS',
  'VALUE',
  'APP_INSTALLS',
  'APP_INSTALLS_AND_OFFSITE_CONVERSIONS',
  /* ── 29 Eyl 2026 (Faz 5 son tur) — v26 `optimization_goal` enum'unun ODAX
     matrisinde yeri olanları (satırlar aşağıda, `[VERIFY]` işaretli). Enum'un
     geri kalanı BİLEREK yok: `NONE`, `DERIVED_EVENTS`, `AUTOMATIC_OBJECTIVE`,
     `ADVERTISER_SILOED_VALUE`, `IN_APP_VALUE` iç/Advantage+ değerleri;
     `ENGAGED_USERS`, `VISIT_INSTAGRAM_PROFILE` eski (yerini `PROFILE_VISIT`
     aldı); `SUBSCRIBERS`, `MEANINGFUL_CALL_ATTEMPT`,
     `MESSAGING_DEEP_CONVERSATION_AND_FOLLOW` için hedef×hedef yeri satırı
     belgede yok — satırsız enum değeri `checkMetaAdSetCombo`da her zaman
     reddedilirdi, yani yalnız yanıltıcı olurdu. */
  'PROFILE_VISIT',
  'PROFILE_AND_PAGE_ENGAGEMENT',
  'ENGAGED_PAGE_VIEWS',
  'REMINDERS_SET',
  'MESSAGING_PURCHASE_CONVERSION',
  'MESSAGING_APPOINTMENT_CONVERSION',
] as const;
export type MetaOptimizationGoal = (typeof META_OPTIMIZATION_GOALS)[number];

export const META_BILLING_EVENTS = [
  'IMPRESSIONS',
  'LINK_CLICKS',
  'THRUPLAY',
  'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS',
] as const;
export type MetaBillingEvent = (typeof META_BILLING_EVENTS)[number];

export const META_BID_STRATEGIES = [
  'LOWEST_COST_WITHOUT_CAP',
  'LOWEST_COST_WITH_BID_CAP',
  'COST_CAP',
  'LOWEST_COST_WITH_MIN_ROAS',
] as const;
export type MetaBidStrategy = (typeof META_BID_STRATEGIES)[number];

/// `WEBSITE` açıkça yazılabilir; yazılmaması da "web sitesi" demek.
export const META_DESTINATION_TYPES = [
  'WEBSITE',
  'APP',
  'MESSENGER',
  'WHATSAPP',
  'INSTAGRAM_DIRECT',
  'PHONE_CALL',
  'ON_AD',
  'ON_POST',
  'ON_PAGE',
  'ON_EVENT',
  'ON_VIDEO',
  'LEAD_FROM_MESSENGER',
  'LEAD_FROM_IG_DIRECT',
  'MESSAGING_MESSENGER_WHATSAPP',
  'MESSAGING_INSTAGRAM_DIRECT_MESSENGER',
  'MESSAGING_INSTAGRAM_DIRECT_WHATSAPP',
  'MESSAGING_INSTAGRAM_DIRECT_MESSENGER_WHATSAPP',
  /* 29 Eyl 2026 (Faz 5 son tur) — v26 `destination_type` enum'undan:
     profil/Sayfa ziyareti (Ads Manager "Instagram ya da Facebook" dönüşüm
     yeri), Shops reklamı ve Advantage+ katalog reklamının uygulama bağlantısı.
     `FACEBOOK` (yalnız Marketplace araç dikeyi), `INSTAGRAM_LIVE`,
     `FACEBOOK_LIVE`, `IMAGINE` BİLEREK yok — ODAX satırları belgede yok. */
  'INSTAGRAM_PROFILE',
  'FACEBOOK_PAGE',
  'INSTAGRAM_PROFILE_AND_FACEBOOK_PAGE',
  'SHOP_AUTOMATIC',
  'APPLINKS_AUTOMATIC',
] as const;
export type MetaDestinationType = (typeof META_DESTINATION_TYPES)[number];

export const META_CUSTOM_EVENT_TYPES = [
  'PURCHASE',
  'ADD_TO_CART',
  'INITIATED_CHECKOUT',
  'ADD_PAYMENT_INFO',
  'ADD_TO_WISHLIST',
  'LEAD',
  'COMPLETE_REGISTRATION',
  'CONTENT_VIEW',
  'SEARCH',
  'CONTACT',
  'CUSTOMIZE_PRODUCT',
  'DONATE',
  'FIND_LOCATION',
  'SCHEDULE',
  'START_TRIAL',
  'SUBMIT_APPLICATION',
  'SUBSCRIBE',
  'SERVICE_BOOKING_REQUEST',
  'LISTING_INTERACTION',
  'RATE',
  'TUTORIAL_COMPLETION',
  'LEVEL_ACHIEVED',
  'ACHIEVEMENT_UNLOCKED',
  'SPENT_CREDITS',
  'OTHER',
] as const;
export type MetaCustomEventType = (typeof META_CUSTOM_EVENT_TYPES)[number];

/**
 * `promoted_object`in bir satırda ne istediği:
 *   none      — hiçbir şey
 *   page      — `page_id`
 *   app       — `application_id` + `object_store_url`
 *   conversion — dönüşüm hedefi: (`pixel_id` + `custom_event_type`) YA DA
 *               özel dönüşüm YA DA katalog ürün seti YA DA uygulama
 *   catalog   — katalog ürün seti (`product_set_id`) ŞART — Advantage+ katalog
 *               reklamının `APPLINKS_AUTOMATIC` hedef yeri (Faz 5 son tur)
 */
export type MetaPromotedRequirement = 'none' | 'page' | 'app' | 'conversion' | 'catalog';

export interface MetaOdaxRule {
  objective: MetaOdaxObjective;
  /// `null` = web sitesi (alan gönderilmez ya da `WEBSITE`).
  destinations: readonly (MetaDestinationType | null)[];
  goals: readonly MetaOptimizationGoal[];
  promoted: MetaPromotedRequirement;
  /// Bu satırda yalnız belirli optimizasyonlar dönüşüm hedefi istiyorsa.
  conversionGoals?: readonly MetaOptimizationGoal[];
}

const WEBSITE: readonly (MetaDestinationType | null)[] = [null, 'WEBSITE'];
/// Ads Manager'ın "Instagram ya da Facebook" dönüşüm yeri — üç API sözcüğü.
const PROFILE_DESTINATIONS: readonly MetaDestinationType[] = [
  'INSTAGRAM_PROFILE',
  'FACEBOOK_PAGE',
  'INSTAGRAM_PROFILE_AND_FACEBOOK_PAGE',
];
const MESSAGING_MULTI: readonly MetaDestinationType[] = [
  'INSTAGRAM_DIRECT',
  'MESSAGING_MESSENGER_WHATSAPP',
  'MESSAGING_INSTAGRAM_DIRECT_MESSENGER',
  'MESSAGING_INSTAGRAM_DIRECT_WHATSAPP',
  'MESSAGING_INSTAGRAM_DIRECT_MESSENGER_WHATSAPP',
];

export const META_ODAX_MATRIX: readonly MetaOdaxRule[] = [
  {
    objective: 'OUTCOME_AWARENESS',
    destinations: WEBSITE,
    goals: ['REACH', 'IMPRESSIONS', 'AD_RECALL_LIFT', 'THRUPLAY', 'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS'],
    promoted: 'page',
  },
  {
    objective: 'OUTCOME_TRAFFIC',
    destinations: WEBSITE,
    goals: ['LINK_CLICKS', 'LANDING_PAGE_VIEWS', 'REACH', 'IMPRESSIONS'],
    promoted: 'none',
  },
  { objective: 'OUTCOME_TRAFFIC', destinations: ['APP'], goals: ['LINK_CLICKS'], promoted: 'app' },
  {
    objective: 'OUTCOME_TRAFFIC',
    destinations: ['MESSENGER'],
    goals: ['LINK_CLICKS', 'REACH', 'IMPRESSIONS'],
    promoted: 'none',
  },
  {
    objective: 'OUTCOME_TRAFFIC',
    destinations: ['WHATSAPP'],
    goals: ['LINK_CLICKS', 'REACH', 'IMPRESSIONS'],
    promoted: 'page',
  },
  {
    objective: 'OUTCOME_TRAFFIC',
    destinations: ['PHONE_CALL'],
    goals: ['QUALITY_CALL', 'LINK_CLICKS'],
    promoted: 'none',
  },
  {
    objective: 'OUTCOME_ENGAGEMENT',
    destinations: ['ON_POST'],
    goals: ['POST_ENGAGEMENT', 'REACH', 'IMPRESSIONS'],
    promoted: 'none',
  },
  { objective: 'OUTCOME_ENGAGEMENT', destinations: ['ON_PAGE'], goals: ['PAGE_LIKES'], promoted: 'page' },
  {
    objective: 'OUTCOME_ENGAGEMENT',
    destinations: ['ON_EVENT'],
    goals: ['EVENT_RESPONSES', 'POST_ENGAGEMENT', 'REACH', 'IMPRESSIONS'],
    promoted: 'none',
  },
  {
    objective: 'OUTCOME_ENGAGEMENT',
    destinations: ['ON_VIDEO'],
    goals: ['THRUPLAY', 'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS'],
    promoted: 'none',
  },
  {
    objective: 'OUTCOME_ENGAGEMENT',
    destinations: ['MESSENGER'],
    goals: ['CONVERSATIONS', 'LINK_CLICKS', 'LEAD_GENERATION'],
    promoted: 'page',
  },
  {
    objective: 'OUTCOME_ENGAGEMENT',
    destinations: ['WHATSAPP'],
    goals: ['CONVERSATIONS', 'LINK_CLICKS'],
    promoted: 'page',
  },
  /// [VERIFY] Çok hedefli mesajlaşma (IG Direct + Messenger + WhatsApp) —
  /// tabloda ayrı satır yok; Ads Manager aynı "sohbet" optimizasyonunu sunuyor.
  {
    objective: 'OUTCOME_ENGAGEMENT',
    destinations: MESSAGING_MULTI,
    goals: ['CONVERSATIONS', 'LINK_CLICKS'],
    promoted: 'page',
  },
  {
    objective: 'OUTCOME_ENGAGEMENT',
    destinations: WEBSITE,
    goals: ['OFFSITE_CONVERSIONS', 'LINK_CLICKS', 'REACH', 'LANDING_PAGE_VIEWS', 'IMPRESSIONS'],
    promoted: 'conversion',
    conversionGoals: ['OFFSITE_CONVERSIONS'],
  },
  {
    objective: 'OUTCOME_APP_PROMOTION',
    destinations: [null, 'APP'],
    goals: [
      'APP_INSTALLS',
      'LINK_CLICKS',
      'OFFSITE_CONVERSIONS',
      'VALUE',
      'APP_INSTALLS_AND_OFFSITE_CONVERSIONS',
    ],
    promoted: 'app',
  },
  {
    objective: 'OUTCOME_LEADS',
    destinations: ['ON_AD'],
    goals: ['LEAD_GENERATION', 'QUALITY_LEAD'],
    promoted: 'page',
  },
  {
    objective: 'OUTCOME_LEADS',
    destinations: ['LEAD_FROM_MESSENGER', 'LEAD_FROM_IG_DIRECT'],
    goals: ['LEAD_GENERATION'],
    promoted: 'page',
  },
  { objective: 'OUTCOME_LEADS', destinations: ['PHONE_CALL'], goals: ['QUALITY_CALL'], promoted: 'page' },
  {
    objective: 'OUTCOME_LEADS',
    destinations: WEBSITE,
    goals: ['OFFSITE_CONVERSIONS', 'LINK_CLICKS'],
    promoted: 'conversion',
    conversionGoals: ['OFFSITE_CONVERSIONS'],
  },
  {
    objective: 'OUTCOME_LEADS',
    destinations: ['APP'],
    goals: ['OFFSITE_CONVERSIONS', 'LINK_CLICKS'],
    promoted: 'app',
  },
  {
    objective: 'OUTCOME_SALES',
    destinations: WEBSITE,
    goals: ['OFFSITE_CONVERSIONS', 'VALUE'],
    promoted: 'conversion',
  },
  { objective: 'OUTCOME_SALES', destinations: ['APP'], goals: ['OFFSITE_CONVERSIONS', 'VALUE'], promoted: 'app' },
  { objective: 'OUTCOME_SALES', destinations: ['MESSENGER'], goals: ['CONVERSATIONS'], promoted: 'page' },
  {
    objective: 'OUTCOME_SALES',
    destinations: ['WHATSAPP'],
    goals: ['LINK_CLICKS', 'CONVERSATIONS', 'OFFSITE_CONVERSIONS', 'IMPRESSIONS', 'REACH'],
    promoted: 'page',
  },
  { objective: 'OUTCOME_SALES', destinations: ['PHONE_CALL'], goals: ['QUALITY_CALL'], promoted: 'page' },

  /* ── 29 Eyl 2026 (Faz 5 son tur) — v26'nın yeni hedef yerleri ve
     optimizasyonları. Meta ajanının "sihirbaz matrisi eksik" bildirimi: bu
     satırlar yokken `meta.adset.create` profil ziyareti, mesajla satın alma,
     hatırlatıcı, Shops ve Advantage+ katalog reklam setlerini Meta'ya hiç
     gitmeden reddediyordu.
     🔴 [VERIFY] HEPSİ: ODAX tablosunda (`ad-campaign-group`) bu satırlar YOK;
     enum değerleri `adset` referansından, hedef eşlemesi Ads Manager'ın dönüşüm
     yeri × performans hedefi menüsünden ve `destination_type` rehberinden
     (Shops → satış, APPLINKS_AUTOMATIC → `product_set_id`). Kuşkulu yerde
     DAR tutuldu; Meta reddederse ret gerekçesi görünür (sessiz geçiş yok). */
  /// Trafik → "Instagram ya da Facebook": profil/Sayfa ziyareti (Sayfa + IG hesabı).
  { objective: 'OUTCOME_TRAFFIC', destinations: PROFILE_DESTINATIONS, goals: ['PROFILE_VISIT'], promoted: 'page' },
  /// Trafik → web sitesi: "etkileşimli sayfa görüntüleme" (açılış sayfası görüntülemenin üst basamağı).
  { objective: 'OUTCOME_TRAFFIC', destinations: WEBSITE, goals: ['ENGAGED_PAGE_VIEWS'], promoted: 'none' },
  /// Etkileşim → "Instagram ya da Facebook": profil ve Sayfa etkileşimi.
  {
    objective: 'OUTCOME_ENGAGEMENT',
    destinations: PROFILE_DESTINATIONS,
    goals: ['PROFILE_AND_PAGE_ENGAGEMENT'],
    promoted: 'page',
  },
  /// Etkileşim → "Reklamınızda" → hatırlatıcı reklamı (yaklaşan etkinlik kreatifte).
  { objective: 'OUTCOME_ENGAGEMENT', destinations: ['ON_POST'], goals: ['REMINDERS_SET'], promoted: 'none' },
  /// Mesajla satın alma — yalnız Messenger'a tıklatan reklam; satın alma olayı Meta'ya gönderilmeli.
  {
    objective: 'OUTCOME_ENGAGEMENT',
    destinations: ['MESSENGER'],
    goals: ['MESSAGING_PURCHASE_CONVERSION'],
    promoted: 'page',
  },
  { objective: 'OUTCOME_SALES', destinations: ['MESSENGER'], goals: ['MESSAGING_PURCHASE_CONVERSION'], promoted: 'page' },
  /// Potansiyel müşteri → Messenger: lead formu sohbette + mesajla randevu
  /// (click-to-Messenger rehberi `MESSENGER` + LEAD_GENERATION/QUALITY_LEAD).
  {
    objective: 'OUTCOME_LEADS',
    destinations: ['MESSENGER'],
    goals: ['LEAD_GENERATION', 'QUALITY_LEAD', 'MESSAGING_APPOINTMENT_CONVERSION'],
    promoted: 'page',
  },
  {
    objective: 'OUTCOME_LEADS',
    destinations: ['LEAD_FROM_MESSENGER'],
    goals: ['MESSAGING_APPOINTMENT_CONVERSION'],
    promoted: 'page',
  },
  /// Shops reklamı — site + Meta mağazası otomatik (dönüşüm hedefi ya da katalog).
  {
    objective: 'OUTCOME_SALES',
    destinations: ['SHOP_AUTOMATIC'],
    goals: ['OFFSITE_CONVERSIONS', 'VALUE'],
    promoted: 'conversion',
  },
  /// Advantage+ katalog reklamı, uygulama bağlantısıyla — ürün seti ŞART.
  {
    objective: 'OUTCOME_SALES',
    destinations: ['APPLINKS_AUTOMATIC'],
    goals: ['OFFSITE_CONVERSIONS', 'VALUE', 'LINK_CLICKS'],
    promoted: 'catalog',
  },
];

/// Optimizasyon → geçerli faturalama olayları (AUCTION). Listede olmayan her
/// optimizasyon YALNIZ `IMPRESSIONS` ile faturalanır.
const META_BILLING_BY_GOAL: Readonly<Partial<Record<MetaOptimizationGoal, readonly MetaBillingEvent[]>>> =
  {
    LINK_CLICKS: ['IMPRESSIONS', 'LINK_CLICKS'],
    THRUPLAY: ['IMPRESSIONS', 'THRUPLAY'],
    TWO_SECOND_CONTINUOUS_VIDEO_VIEWS: ['IMPRESSIONS', 'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS'],
  };

export function metaBillingEventsFor(goal: string): readonly MetaBillingEvent[] {
  return META_BILLING_BY_GOAL[goal as MetaOptimizationGoal] ?? ['IMPRESSIONS'];
}

/** Hedef için geçerli optimizasyonlar (tüm hedef yerlerinin birleşimi). */
export function metaGoalsFor(objective: string, destination?: string | null): MetaOptimizationGoal[] {
  const dest = (destination === 'WEBSITE' ? null : (destination ?? null)) as MetaDestinationType | null;
  const goals = new Set<MetaOptimizationGoal>();
  for (const rule of META_ODAX_MATRIX) {
    if (rule.objective !== objective) continue;
    if (destination !== undefined && !rule.destinations.includes(dest)) continue;
    for (const goal of rule.goals) goals.add(goal);
  }
  return [...goals];
}

export interface MetaAdSetComboInput {
  objective: string;
  destinationType?: string | null;
  optimizationGoal: string;
  billingEvent: string;
  promotedObject?: {
    pixelId?: string;
    customEventType?: string;
    customConversionId?: string;
    pageId?: string;
    applicationId?: string;
    objectStoreUrl?: string;
    productSetId?: string;
    productCatalogId?: string;
  } | null;
  /// Etkin teklif stratejisi (CBO'da kampanyanın, ABO'da ad set'in).
  bidStrategy?: string | null;
  bidAmount?: number | null;
  roasFloor?: number | null;
}

/**
 * Ad set kombinasyonunu Meta'ya gitmeden doğrular — ihlal listesi (boş = geçerli).
 * Mesajlar kullanıcıya/modele gider: NE yanlış + NE yapılmalı.
 */
export function checkMetaAdSetCombo(input: MetaAdSetComboInput): string[] {
  const problems: string[] = [];
  const dest = (
    input.destinationType === undefined ||
    input.destinationType === null ||
    input.destinationType === 'WEBSITE'
      ? null
      : input.destinationType
  ) as MetaDestinationType | null;

  const rules = META_ODAX_MATRIX.filter((r) => r.objective === input.objective);
  if (rules.length === 0) {
    problems.push(`Bilinmeyen Meta hedefi "${input.objective}" (${META_ODAX_OBJECTIVES.join(', ')})`);
    return problems;
  }
  const rule = rules.find(
    (r) => r.destinations.includes(dest) && r.goals.includes(input.optimizationGoal as MetaOptimizationGoal),
  );
  if (!rule) {
    const destRules = rules.filter((r) => r.destinations.includes(dest));
    if (destRules.length === 0) {
      const allowed = [...new Set(rules.flatMap((r) => r.destinations.map((d) => d ?? 'WEBSITE')))];
      problems.push(
        `${input.objective} hedefinde "${dest ?? 'WEBSITE'}" hedef yeri (destination_type) kullanılamaz — geçerli: ${allowed.join(', ')}`,
      );
    } else {
      const allowed = [...new Set(destRules.flatMap((r) => r.goals))];
      problems.push(
        `${input.objective} / ${dest ?? 'WEBSITE'} için "${input.optimizationGoal}" optimizasyonu geçersiz — geçerli: ${allowed.join(', ')}`,
      );
    }
    return problems;
  }

  const billing = metaBillingEventsFor(input.optimizationGoal);
  if (!billing.includes(input.billingEvent as MetaBillingEvent)) {
    problems.push(
      `"${input.optimizationGoal}" optimizasyonu "${input.billingEvent}" ile faturalanamaz — geçerli: ${billing.join(', ')}`,
    );
  }

  const po = input.promotedObject ?? {};
  const hasApp = Boolean(po.applicationId && po.objectStoreUrl);
  const needsConversion =
    rule.promoted === 'conversion' &&
    (rule.conversionGoals ?? rule.goals).includes(input.optimizationGoal as MetaOptimizationGoal);
  if (rule.promoted === 'page' && !po.pageId) {
    problems.push(`${input.objective} / ${dest ?? 'WEBSITE'} için promotedObject.pageId (Facebook Sayfası) zorunlu`);
  }
  if (rule.promoted === 'app' && !hasApp) {
    problems.push(
      `${input.objective} / ${dest ?? 'WEBSITE'} için promotedObject.applicationId + objectStoreUrl (uygulama mağaza adresi) zorunlu`,
    );
  }
  if (rule.promoted === 'catalog' && !po.productSetId) {
    problems.push(
      `${input.objective} / ${dest ?? 'WEBSITE'} için promotedObject.productSetId (katalog ürün seti — meta.product_set.list / meta.product_set.create) zorunlu`,
    );
  }
  if (needsConversion) {
    const pixelEvent = Boolean(po.pixelId && po.customEventType);
    const customConversion = Boolean(po.customConversionId && po.pixelId);
    const catalog = Boolean(po.productSetId);
    if (po.pixelId && !po.customEventType && !po.customConversionId) {
      problems.push('Piksel verildi ama optimize edilecek olay yok: promotedObject.customEventType zorunlu (ör. PURCHASE, LEAD)');
    } else if (!pixelEvent && !customConversion && !catalog && !hasApp) {
      problems.push(
        `"${input.optimizationGoal}" dönüşüm hedefi ister: promotedObject.pixelId + customEventType (ör. PURCHASE, LEAD) — ya da customConversionId + pixelId, ya da katalog productSetId`,
      );
    }
  }

  const strategy = input.bidStrategy ?? 'LOWEST_COST_WITHOUT_CAP';
  if (strategy === 'LOWEST_COST_WITH_MIN_ROAS') {
    if (input.optimizationGoal !== 'VALUE') {
      problems.push('LOWEST_COST_WITH_MIN_ROAS yalnız VALUE optimizasyonuyla kullanılabilir');
    }
    if (input.roasFloor == null) problems.push('LOWEST_COST_WITH_MIN_ROAS için roasFloor (en düşük ROAS) zorunlu');
    if (input.bidAmount != null) problems.push('LOWEST_COST_WITH_MIN_ROAS ile bidAmount gönderilmez');
  } else if (strategy === 'COST_CAP' || strategy === 'LOWEST_COST_WITH_BID_CAP') {
    if (input.bidAmount == null) {
      problems.push(`${strategy} için ad set'te bidAmount (hedef maliyet / teklif tavanı) zorunlu`);
    }
    if (input.roasFloor != null) problems.push(`${strategy} ile roasFloor gönderilmez`);
  } else if (input.bidAmount != null || input.roasFloor != null) {
    problems.push(
      'LOWEST_COST_WITHOUT_CAP (en düşük maliyet) teklif tutarı/ROAS tabanı almaz — COST_CAP, LOWEST_COST_WITH_BID_CAP ya da LOWEST_COST_WITH_MIN_ROAS seçin',
    );
  }
  return problems;
}

/**
 * Reklam eylem düğmesi (`publishSchema.ctaType` / `rmfAdCreativeSchema.ctaType`).
 *
 * Dar bir küme, Meta'nın tamamı değil — ELLE yazıldı. Serbest metin
 * bırakılsaydı yazım hatası platform reddiyle son adımda ortaya çıkardı.
 */
export const AD_CTA_TYPES = [
  'LEARN_MORE',
  'SHOP_NOW',
  'SIGN_UP',
  'CONTACT_US',
  'DOWNLOAD',
  'BOOK_TRAVEL',
] as const;
export type AdCtaType = (typeof AD_CTA_TYPES)[number];

// ═══════════════════════════════════════════════════════════════════════════
// Platform başına ALAN ZORUNLULUKLARI
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 SÖZLEŞME İLE ADAPTER ARASINDAKİ FARK — HİÇBİR DOKÜMANDA YAZMIYORDU.
 *
 * `createAdSetSchema` üç alanı isteğe bağlı tanımlıyor: `targeting.countries`,
 * `optimizationGoal`, `billingEvent`. Ama adapter'lar aynı fikirde değil:
 *
 *   · Meta ÜLKESİZ AD SET'İ REDDEDİYOR. `meta.connector.ts` `createAdSet`
 *     ülke listesi boşsa fırlatıyor ve gerekçesi kodda yazılı: "US gibi bir
 *     varsayılan UYDURMUYORUZ — yanlış ülkeye reklam vermek, reklam
 *     vermemekten pahalı." Yani sözleşmede isteğe bağlı olan alan Meta
 *     akışında PRATİKTE ZORUNLU.
 *   · Meta `optimization_goal` ve `billing_event` olmadan ad set açmıyor.
 *   · Google aynı iki alan gönderilirse `FIELD_NOT_SUPPORTED_ON_PLATFORM`
 *     ile REDDEDİYOR.
 *
 * Bu tabloyu arayüzün içine gömmek, üç ekranın (form doğrulama, özet, hata
 * mesajı) aynı kuralı üç kez yorumlaması demekti. Tek yerde.
 */
export interface WizardFieldRequirements {
  /// `targeting.countries` en az bir ülke istiyor mu.
  countriesRequired: boolean;
  /// `optimizationGoal` + `billingEvent` zorunlu mu.
  optimizationRequired: boolean;
  /// `optimizationGoal` + `billingEvent` GÖNDERİLEMEZ mi (gönderilirse ret).
  optimizationForbidden: boolean;
  /**
   * `identityRef` (Meta'da Sayfa kimliği) pratikte zorunlu mu.
   *
   * Şemada isteğe bağlı çünkü MEVCUT bir ad set'e ikinci reklam eklenirken
   * adapter kimliği kardeş reklamdan devralıyor. Ama sihirbaz YENİ bir ad set
   * açıyor ve o ad set'in İLK reklamında devralınacak hiçbir şey yok —
   * adapter açık bir hatayla düşüyor. Sihirbaz bu yüzden alanı zorunlu kılar.
   */
  identityRefRequired: boolean;
}

export const WIZARD_FIELD_REQUIREMENTS: Readonly<Record<string, WizardFieldRequirements>> = {
  META: {
    countriesRequired: true,
    optimizationRequired: true,
    optimizationForbidden: false,
    identityRefRequired: true,
  },
  GOOGLE: {
    countriesRequired: false,
    optimizationRequired: false,
    optimizationForbidden: true,
    identityRefRequired: false,
  },
  /* 29 Eyl 2026 (Faz 5) — üç adapter'ın gövdelerinden okundu:
     · LinkedIn: konum facet'i + dil (`readLocale`) ülkesiz kurulamıyor; boş
       optimizasyon hedeften varsayılan alıyor; şirket sayfası reklam setinde.
     · TikTok: `adGroupBody` LOCATION_REQUIRED; Spark kimliği zorunlu.
     · X: ülkesiz reklam seti TÜM DÜNYA → adapter reddediyor; kimlik hesabın
       FULL kullanıcısından çözülüyor (zorunlu değil). */
  LINKEDIN: {
    countriesRequired: true,
    optimizationRequired: false,
    optimizationForbidden: false,
    identityRefRequired: true,
  },
  TIKTOK: {
    countriesRequired: true,
    optimizationRequired: true,
    optimizationForbidden: false,
    identityRefRequired: true,
  },
  TWITTER: {
    countriesRequired: true,
    optimizationRequired: true,
    optimizationForbidden: false,
    identityRefRequired: false,
  },
};

/// Tanınmayan platformda en gevşek kural: hiçbir şeyi zorunlu kılma, hiçbir
/// şeyi yasaklama. Uydurma bir zorunluluk, kullanıcıyı dolduramayacağı bir
/// alanın önünde durdurur.
const PERMISSIVE_REQUIREMENTS: WizardFieldRequirements = {
  countriesRequired: false,
  optimizationRequired: false,
  optimizationForbidden: false,
  identityRefRequired: false,
};

export function wizardFieldRequirements(platform: string): WizardFieldRequirements {
  return WIZARD_FIELD_REQUIREMENTS[platform] ?? PERMISSIVE_REQUIREMENTS;
}

// ═══════════════════════════════════════════════════════════════════════════
// Marka kimliği (Meta Sayfa kimliği) — nerede saklanır
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 SAKLANACAK BİR ALAN YOK VE ŞEMA DONMUŞ.
 *
 * Meta Sayfa kimliği her yeni ad set'in ilk reklamında gerekiyor ama ne
 * `Workspace`ta ne `AdAccount`ta onu tutacak bir kolon var; sayfa listesini
 * çekmek de mümkün değil (`pages_show_list` izni OAuth kapsamlarımızda yok).
 *
 * Üç seçenek vardı: (1) her seferinde elle yazdırmak — kullanıcının
 * `1315580038304394` gibi bir numarayı ezberlemesi, (2) tarayıcıda hatırlamak,
 * (3) şemaya alan eklemek — YASAK, yönetici kararı.
 *
 * (2) seçildi ve sınırı EKRANDA YAZILI olmak zorunda: değer bu tarayıcıda
 * kalır, ekip arkadaşı aynı numarayı kendi tarayıcısına ilk kez girer. Alan
 * HER ZAMAN düzenlenebilir kalır — hatırlanan değer bir öneri, bir kilit değil.
 *
 * Anahtar hesap başına: aynı çalışma alanında iki reklam hesabı iki farklı
 * Sayfa'ya bağlı olabilir ve tek anahtar, ikincisinde yanlış sayfayı önerirdi.
 */
export function identityRefStorageKey(adAccountId: string): string {
  return `mentorivo.wizard.identityRef.${adAccountId}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// Kreatif seçici
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Seçicinin `GET /creatives` çağrısında kullanacağı süzgeç.
 *
 * 🔴 KREATİF TEK KULLANIMLIK. `creative.service.publish` yalnız
 * `status: READY` ve `s3Key` dolu kreatifi kabul ediyor; yayınlandıktan sonra
 * satır `PUBLISHED`a çekiliyor ve ikinci kez yayınlanamıyor
 * (`ALREADY_PUBLISHED`). Yani seçici `READY` süzmek ZORUNDA — süzmezse
 * kullanıcı seçebildiği bir kreatifle 409 alır.
 *
 * ⚠️ ÜRÜN BOŞLUĞU, KAPATILMADI: kullanıcı aynı görseli iki kampanyada
 * kullanmak isterse cevap yok. Bu kısıt seçicinin yanında BİR CÜMLEYLE
 * yazılır ("yayınlanan kreatif bu listeden düşer") ve stüdyodaki kopya/iyileştir
 * akışına bağlantı verilir. Sessizce listeden düşürmek, kullanıcının
 * kreatifinin silindiğini sanması demek.
 */
export const WIZARD_CREATIVE_FILTER = { status: 'READY' } as const;

// ═══════════════════════════════════════════════════════════════════════════
// Hata dallanması
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sihirbazın tanıdığı hata kodları — ÜÇ FARKLI SÖZLÜKTEN geliyorlar.
 *
 * Arayüz `code` üzerinden dallanır, `message` yalnız gösterimdir. Bu birleşik
 * liste ikinci bir sözlük DEĞİL: değerler `RmfErrorCode` ve
 * `CreativeErrorCode`tan OKUNUYOR, burada yeniden yazılmıyor. Yeniden
 * yazılsaydı bir gün biri sapar ve ekran o kodu tanımaz hâle gelirdi.
 */
export const WIZARD_ERROR_CODES = {
  ...RmfErrorCode,
  /// Kreatif hazır değil (üretim bitmemiş / dosya yok).
  SOURCE_NOT_READY: CreativeErrorCode.SOURCE_NOT_READY,
  /// Kreatif zaten yayınlanmış — seçici süzmediyse buraya düşülür.
  ALREADY_PUBLISHED: CreativeErrorCode.ALREADY_PUBLISHED,
  /**
   * 🔴 EKİP ONAYI KAPISI — SİHİRBAZIN SESSİZ KİLİDİ.
   *
   * `creative.service.publish` platform çağrısından ÖNCE
   * `approvals.assertPublishable` çağırıyor. Çalışma alanı onay istiyorsa
   * (`Workspace.creativeApprovalRequired`) yayın 403 ile engelleniyor.
   *
   * Bu dalın KENDİ SONU var ve plan onu hiç anmıyor: sihirbaz "kampanya
   * hazır" ile değil "kreatif onaya gönderildi" ile biter. Kampanya ve ad set
   * gerçekten oluştu, duraklatılmış duruyor; eksik olan yalnız reklam.
   * Kullanıcıya söylenecek şey bu — "bir hata oluştu" değil.
   */
  APPROVAL_REQUIRED: CreativeErrorCode.APPROVAL_REQUIRED,
} as const;
export type WizardErrorCode = (typeof WIZARD_ERROR_CODES)[keyof typeof WIZARD_ERROR_CODES];

/**
 * Kullanıcının bu hatadan sonra ne yapabileceği.
 *
 * Ekranın en önemli kararı bu: aynı düğme bazı kodlarda "tekrar dene",
 * bazılarında "tekrar DENEME" demek zorunda.
 */
export const WizardRecovery = {
  /// Aynı gövdeyle, AYNI anahtarla yeniden denenebilir (geçici arıza).
  RETRY: 'retry',
  /// Girdi düzeltilmeli; düzeltince YENİ anahtar üretilir.
  FIX_INPUT: 'fix_input',
  /**
   * 🔴 TEKRAR DENEME — ÖNCE PLATFORMU KONTROL ET.
   *
   * `PLATFORM_OUTCOME_UNKNOWN`: platform çağrısı zaman aşımıyla bitti, varlık
   * OLUŞMUŞ OLABİLİR. Aynı anahtarla ikinci istek çalıştırılmadan 409'a
   * düşüyor. "Tekrar dene" düğmesi göstermek, kullanıcıya ikinci bir kampanya
   * açtırma riski demek.
   */
  CHECK_PLATFORM: 'check_platform',
  /// Bağlantı onarılmalı (yeniden yetkilendirme).
  RECONNECT: 'reconnect',
  /// Bekleyip yeniden denemeli (kota).
  WAIT: 'wait',
  /// Akış burada BİTİYOR; hata değil, farklı bir son.
  ENDS_HERE: 'ends_here',
} as const;
export type WizardRecovery = (typeof WizardRecovery)[keyof typeof WizardRecovery];

export const WIZARD_ERROR_RECOVERY: Readonly<Record<string, WizardRecovery>> = {
  [RmfErrorCode.PLATFORM_CANNOT_CREATE]: WizardRecovery.ENDS_HERE,
  [RmfErrorCode.PARENT_NOT_FOUND]: WizardRecovery.FIX_INPUT,
  [RmfErrorCode.PLATFORM_AUTH_FAILED]: WizardRecovery.RECONNECT,
  [RmfErrorCode.RATE_LIMITED]: WizardRecovery.WAIT,
  [RmfErrorCode.PLATFORM_UNAVAILABLE]: WizardRecovery.RETRY,
  [RmfErrorCode.PLATFORM_REJECTED]: WizardRecovery.FIX_INPUT,
  /**
   * 🔴 Varsayılana (RETRY) DÜŞMEMELİ. `wizardRecovery` tanımadığı kodu
   * "tekrar dene" sayıyor; bu kodda aynı gövde her seferinde aynı reddi alır.
   * Kullanıcının yapması gereken değeri düzeltmek değil, alanı KALDIRMAK —
   * ama iki eylem de aynı ekranı (formu düzenle) açtığı için `FIX_INPUT`.
   */
  [RmfErrorCode.FIELD_NOT_SUPPORTED_ON_PLATFORM]: WizardRecovery.FIX_INPUT,
  [RmfErrorCode.PLATFORM_ERROR]: WizardRecovery.RETRY,
  /// 201 ile gelir, hata değil — uyarı. Akış DURUR: bir sonraki adım
  /// `campaignId` istiyor ve elimizde `id: null` var.
  [RmfErrorCode.LOCAL_RECORD_NOT_WRITTEN]: WizardRecovery.ENDS_HERE,
  [RmfErrorCode.PLATFORM_OUTCOME_UNKNOWN]: WizardRecovery.CHECK_PLATFORM,
  [CreativeErrorCode.SOURCE_NOT_READY]: WizardRecovery.FIX_INPUT,
  [CreativeErrorCode.ALREADY_PUBLISHED]: WizardRecovery.FIX_INPUT,
  [CreativeErrorCode.APPROVAL_REQUIRED]: WizardRecovery.ENDS_HERE,
  /**
   * Demo bağlantı seçilmiş. `RETRY` varsayılanı burada zararlı olurdu:
   * aynı hesapla kaç kez denenirse denensin cevap değişmez. Çözüm gerçek
   * bir reklam hesabı seçmek — yani forma dönmek.
   */
  [IntegrationErrorCode.DEMO_CONNECTION_NOT_CALLABLE]: WizardRecovery.FIX_INPUT,
};

export function wizardRecovery(code: string | undefined): WizardRecovery {
  if (!code) return WizardRecovery.RETRY;
  return WIZARD_ERROR_RECOVERY[code] ?? WizardRecovery.RETRY;
}

/**
 * Hata kodunun i18n anahtarı.
 *
 * 🔴 `t.has()` İLE KORUNMADAN BASILMAZ. Sunucu anahtar gönderir, ekran
 * çevirir; sözlükte olmayan bir kod ham dize olarak ekrana düşer.
 *
 * Sözlük durumu (29 Ağu 2026): `rmf.errors` altında EN ve TR'de 14 anahtar
 * var ve ikisi eşit. Burada eskiden `PLATFORM_OUTCOME_UNKNOWN` ve
 * `APPROVAL_REQUIRED` eksik yazıyordu — ikisi de eklendi, en son
 * `FIELD_NOT_SUPPORTED_ON_PLATFORM`. Yeni bir `RmfErrorCode` eklerken
 * anahtar İKİ sözlüğe birden girmeli; biri unutulursa o dilde ham kod düşer.
 */
export function wizardErrorKey(code: string | undefined): string {
  return `rmf.errors.${code ?? RmfErrorCode.PLATFORM_ERROR}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hız sınırı ve idempotency
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `/rmf/*` uçları DAKİKADA 10 istek kabul ediyor (durum/bütçe uçlarının
 * yarısı). Dört adımlık bir sihirbazda kullanıcı hata alıp düzeltip yeniden
 * gönderdiğinde her düzeltme YENİ bir çağrı demek — 10 gerçekçi bir tavan.
 *
 * Bunun iki sonucu var ve ikisi de arayüzün işi:
 *   1. Adım düğmesi gönderim sürerken KİLİTLENİR. Çift tıklama, aynı gövdeyle
 *      ikinci bir çağrı ve boşa harcanan bir kota demek.
 *   2. HTTP 429'un KENDİ mesajı olmalı. O yanıt throttler'dan geliyor,
 *      `RmfErrorCode.RATE_LIMITED` gövdesiyle DEĞİL — ayrı bir kod yolu.
 */
export const RMF_RATE_LIMIT_PER_MINUTE = 10;

/**
 * `Idempotency-Key` semantiği (`creative/idempotency.ts`):
 *
 *   · aynı anahtar + aynı gövde  → saklanan yanıt döner (güvenli tekrar)
 *   · aynı anahtar + FARKLI gövde → 400 `IDEMPOTENCY_KEY_REUSED`
 *   · iş hâlâ sürüyor            → 409 `IDEMPOTENCY_IN_PROGRESS`
 *
 * 🔴 ARAYÜZÜN KURALI: anahtar ADIM BAŞINA üretilir ve gövde DEĞİŞMEDİĞİ
 * sürece SABİT kalır (ağ hatasında güvenle tekrar edilebilsin); kullanıcı
 * formu düzenleyip yeniden gönderdiğinde YENİLENİR (yoksa 400 alır).
 *
 * En az 8 karakter zorunlu. `crypto.randomUUID()` her iki koşulu da sağlıyor.
 */
export const MIN_IDEMPOTENCY_KEY_LENGTH = 8;

// ═══════════════════════════════════════════════════════════════════════════
// Yayına alma onayı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Son adımın onay kartına giden özet.
 *
 * 🔴 Bu onay PARA HARCATIYOR ve metni bunu AÇIKÇA yazmak zorunda: günlük
 * tutar ve HESABIN para birimi (bugün TRY). "Yayına al" demek yetmez —
 * kullanıcının ilk gerçek denemesi 75 ₺/gün × 4 gün.
 *
 * `endTime` yoksa `null`: süresiz kampanya, kullanıcı elle durdurana kadar
 * harcar ve onay kartı bunu ayrıca söyler.
 */
export interface WizardLaunchSummary {
  campaignName: string;
  adSetName: string;
  /// Hesabın para birimi — `AdAccountRow.currency`. Uydurulmaz.
  currency: string;
  dailyBudget: number | null;
  lifetimeBudget: number | null;
  /// ISO datetime. `null` = süresiz.
  endTime: string | null;
  /// Bilinen bitiş varsa toplam tahmini harcama; hesaplanamıyorsa `null`.
  /// ⚠️ Bir GARANTİ değil bir tahmin — platform günlük bütçeyi %25'e kadar
  /// aşabiliyor ve metin bunu söylemeli.
  estimatedTotal: number | null;
}

/**
 * Yayına alma çağrısının gövdesi `entityStatusChangeSchema` — sihirbazda
 * hedef TEK kampanya, `status: 'ACTIVE'`.
 *
 * ⚠️ AD SET VE REKLAM DA DURAKLATILMIŞ DOĞUYOR. Yalnız kampanyayı ACTIVE
 * yapmak, hiyerarşinin altı duraklatılmış kaldığı için hiçbir şey
 * yayınlamaz. Onay üç hedefi birden gönderir (kampanya + ad set + reklam) ve
 * bu tek çağrıya sığar (`targets` en çok 100). Kısmi başarı yine mümkün:
 * `BulkActionResult.failed` doluysa hangi seviyenin açılamadığı SATIRA
 * SATIRA yazılır — "yayına alındı" demek, biri reddedildiyse yalan olur.
 */
export const LAUNCH_TARGET_LEVELS = ['CAMPAIGN', 'ADSET', 'AD'] as const;
